import React, { FunctionComponent, ReactNode } from 'react'

import { useField } from 'formik'
import { NumberFormatValues, PatternFormatProps } from 'react-number-format'
import { SpaceProps, WidthProps } from 'styled-system'

import PatternInput from '@/atoms/input/pattern-input'
import { AdornmentPosition } from '@/models/common'

import FieldWrapper from './field-wrapper'

interface OwnProps {
  label?: string
  name?: string
  fieldProps?: SpaceProps & WidthProps
  onChange?: (val: NumberFormatValues) => void
  onBlur?: () => void
  adornment?: ReactNode
  adornmentPosition?: AdornmentPosition
  isDisabled?: boolean
  hideErrorMessage?: boolean
  tooltip?: string
  format?: string
  mask?: string | string[]
  allowEmptyFormatting?: boolean
}

export type PatternInputProps = Omit<OwnProps & Omit<PatternFormatProps, 'format'>, 'onChange'> & {
  onChange?: (val: NumberFormatValues) => void
  format?: string
}

const PatternInputField: FunctionComponent<PatternInputProps> = ({
  label,
  fieldProps,
  name,
  onChange,
  hideErrorMessage,
  onBlur,
  tooltip,
  ...rest
}) => {
  const [field, meta, helpers] = useField(name)
  const hasError: boolean = meta.touched && !!meta.error

  const handleInputChange = (values: NumberFormatValues) => {
    helpers && helpers.setValue(values.value)
    onChange && onChange(values)
  }

  const handleInputBlur = (): void => {
    helpers && helpers.setTouched(true)
    onBlur && onBlur()
  }

  return (
    <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} hideErrorMessage={hideErrorMessage} tooltip={tooltip}>
      <PatternInput
        id={field.name}
        hasError={hasError}
        name={field.name}
        value={field.value}
        onValueChange={handleInputChange}
        onBlur={handleInputBlur}
        {...rest}
      />
    </FieldWrapper>
  )
}

PatternInputField.displayName = 'PatternInputField'

export default PatternInputField
